import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Avatar,
} from "@mui/material";
import { CheckCircle, Cancel, Refresh } from "@mui/icons-material";
import axiosInstance from "../../utils/axios";
import { useSocket } from "../../context/SocketContext";
import { toast } from "react-toastify";

export default function CodSettlements() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const { socket } = useSocket();

  const fetchSettlements = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/api/delivery/restaurant/cod-settlements");
      setOrders(res.data.orders || []);
    } catch (err) {
      toast.error("Failed to load COD settlements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettlements();
  }, []);

  // Real-time Socket Listener when Delivery Boy clicks "Pay to Restaurant"
  useEffect(() => {
    if (!socket) return;

    const handleNewRequest = (data) => {
      toast.info(`💵 Delivery Boy ${data.deliveryBoyName || ""} sent COD payment request for ₹${data.amount}!`);
      fetchSettlements();
    };

    socket.on("cod-settlement-requested", handleNewRequest);
    return () => socket.off("cod-settlement-requested", handleNewRequest);
  }, [socket]);

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      const res = await axiosInstance.put(`/api/delivery/restaurant/cod-settlements/${orderId}/status`, {
        status: newStatus,
      });
      toast.success(res.data.message);
      setOrders((prev) =>
        prev.map((o) => (o._id === orderId ? res.data.order : o))
      );
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  const totalUnderReview = orders
    .filter((o) => o.codSettlement?.status === "UNDER_REVIEW")
    .reduce((sum, o) => sum + (o.orderSummary?.totalAmount || 0), 0);

  const totalUnsettled = orders
    .filter((o) => !o.codSettlement?.status || ["UNSETTLED", "REJECTED"].includes(o.codSettlement?.status))
    .reduce((sum, o) => sum + (o.orderSummary?.totalAmount || 0), 0);

  const totalPaid = orders
    .filter((o) => o.codSettlement?.status === "PAID")
    .reduce((sum, o) => sum + (o.orderSummary?.totalAmount || 0), 0);

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Delivery Boy COD Cash Settlements
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Verify and approve COD cash collected by delivery partners
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchSettlements}>
          Refresh
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item size={{xs:12, sm:4}}>
          <Card sx={{ bgcolor: "#fffbeb", borderLeft: "5px solid #f59e0b" }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Under Review (Approval Pending)</Typography>
              <Typography variant="h4" fontWeight="bold" color="#d97706" mt={1}>
                ₹{totalUnderReview}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item size={{xs:12, sm:4}}>
          <Card sx={{ bgcolor: "#fef2f2", borderLeft: "5px solid #dc2626" }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Unsettled Cash with Riders</Typography>
              <Typography variant="h4" fontWeight="bold" color="error.main" mt={1}>
                ₹{totalUnsettled}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item size={{xs:12, sm:4}}>
          <Card sx={{ bgcolor: "#f0fdf4", borderLeft: "5px solid #16a34a" }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary">Total Cash Received (Paid)</Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main" mt={1}>
                ₹{totalPaid}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Orders Table */}
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead sx={{ bgcolor: "grey.100" }}>
            <TableRow>
                 <TableCell><strong>#</strong></TableCell>
              <TableCell><strong>Order ID & Date</strong></TableCell>
              <TableCell><strong>Delivery Boy Details</strong></TableCell>
              <TableCell><strong>Customer</strong></TableCell>
              <TableCell><strong>COD Amount</strong></TableCell>
              <TableCell><strong>Settlement Status</strong></TableCell>
              <TableCell align="center"><strong>Actions (Paid / Cancel)</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order, index) => {
              const status = order.codSettlement?.status || "UNSETTLED";
              return (
                <TableRow key={order._id} index={order._id+1} hover>
                  <TableCell>
                    <Typography fontWeight="bold" color="primary">
                       #{index + 1}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    
                    <Typography fontWeight="bold" color="primary">
                      #{order._id.slice(-6).toUpperCase()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(order.createdAt).toLocaleString("en-IN")}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar src={order.deliveryBoy?.profileImage?.url} sx={{ width: 38, height: 38 }} />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {order.deliveryBoy?.name || "N/A"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          📞 {order.deliveryBoy?.phone || "N/A"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          🛵 {order.deliveryBoy?.vehicleNumber || "N/A"}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2">{order.customer?.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{order.customer?.phone}</Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="subtitle1" fontWeight="bold" color="error.main">
                      ₹{order.orderSummary?.totalAmount || 0}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={status}
                      color={
                        status === "PAID"
                          ? "success"
                          : status === "UNDER_REVIEW"
                          ? "warning"
                          : status === "REJECTED"
                          ? "error"
                          : "default"
                      }
                      size="small"
                      sx={{ fontWeight: "bold" }}
                    />
                  </TableCell>

                  <TableCell align="center">
                    {status !== "PAID" ? (
                      <Box display="flex" justifyContent="center" gap={1}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircle />}
                          onClick={() => handleStatusUpdate(order._id, "PAID")}
                        >
                          Paid
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Cancel />}
                          onClick={() => handleStatusUpdate(order._id, "REJECTED")}
                        >
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="success.main" fontWeight="bold">
                        Settled on {order.codSettlement?.settledAt ? new Date(order.codSettlement.settledAt).toLocaleDateString() : ""}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}

            {orders.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  No COD delivery orders found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}